import { useAuth } from '../contexts/AuthContext';
import { useLoading } from '../contexts/LoadingContext';

export const useFetch = () => {
  const { logout } = useAuth();
  const { setLoading } = useLoading();

  // Centralized URL builder to avoid accidental `...undefined` requests
  const buildUrl = (endpoint: string) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) {
      throw new Error('REACT_APP_BACKEND_URL is not defined');
    }
    if (!endpoint) {
      throw new Error('Request endpoint is empty');
    }
    return base + endpoint;
  };

  const postData = async <T,>(endpoint: string, data: T): Promise<T> => {
    // post data and return the response body
    // the response body does not contain any information about the status code
    return typedPostData<T, T>(endpoint, data);
  };

  const typedPostData = async <T, R>(endpoint: string, data: T): Promise<R> => {
    try {
      setLoading(true);
      const response = await fetch(buildUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });

      setLoading(false);
      if (response.status === 401) {
        logout();
      }

      if (!response.ok) {
        throw new Error("Network Error!");
      }

      const result: R = await response.json();
      return result;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const postDataResponse = async <T,>(endpoint: string, data: T): Promise<Response> => {
    // post data and return the response directly
    // the response can be used to check the status code
    try {
      setLoading(true);
      const response = await fetch(buildUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });

      setLoading(false);

      if (response.status === 401) {
        logout();
      }

      return response;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const postFile = async (endpoint: string, data: FormData) => {
    try {
      setLoading(true);
      const response = await fetch(buildUrl(endpoint), {
        method: 'POST',
        body: data,
        credentials: 'include',
      });

      setLoading(false);

      if (response.status === 401) {
        logout();
      }

      if (!response.ok) {
        throw new Error("Network Error!");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }

  const getData = async <T,>(endpoint: string): Promise<T> => {
    try {
      setLoading(true);
      const response = await fetch(buildUrl(endpoint), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
      });

      setLoading(false);

      if (response.status === 401) {
        logout();
      }

      if (!response.ok) {
        throw new Error("Network Error!");
      }

      const result: T = await response.json();
      return result;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  return { postData, getData, postFile, postDataResponse, typedPostData };
};
